import React, { FC, lazy, memo, Suspense, useEffect, useMemo } from 'react'

import { useSetFocus } from '@please/lrud'
import { useLocation, useNavigate } from 'react-router-dom'
import { Element, scroller } from 'react-scroll'

import { getHomePage, toggleAppSettings } from '@/actions/actions'
import { Broadcast } from '@/components/Broadcast/Broadcast'
import { Button } from '@/components/Button/Button'
import { ContentUnavailable } from '@/components/ContentUnavailable/ContentUnavailable'
import { Header } from '@/components/Header'
import { PageBackground } from '@/components/PageContent/PageBackground'
import { constantsBroadcastIds } from '@/constants/common'
import { focusIds, orientation } from '@/constants/focus'
import { paths } from '@/constants/paths'
import { scrollOptions } from '@/constants/scrollers'
import { ContentUnavailableWrapper } from '@/containers/HomePage/styles'
import { Container, ScrollContainer } from '@/containers/styles'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { useFindNearestByImgWidth } from '@/hooks/useFindNearestByImgWidth'
import { useIsBroadcastVisible } from '@/hooks/useIsBroadcastVisible'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import useStore from '@/store/useStore'
import { LinesGroupType } from '@/types/sliderTypes'

const LinesGroup: FC<LinesGroupType> = lazy(
  () => import('@/components/LinesGroup/LinesGroup'),
)

const focusId = `${focusIds.CLASSIC_HOME}${focusIds.CONTAINER_MOD}`

const HomeRaw: FC = () => {
  const navigate = useNavigate()
  const setFocus = useSetFocus()
  const { t } = useCommonTranslation()
  const { search } = useLocation()
  const { page, setPage } = useStore((state) => state.page)
  const appSettings = useStore((state) => state.common.appSettings)

  const classicHome = page?.classicHome
  const memoizedLines = useMemo(() => classicHome?.lines || [], [page])

  const { defaultFocusChild, onMove } = useCustomFocus({
    focusId,
    scrollOptions: scrollOptions.HomeScroll,
    startFocusChild: page?.anchorId || page?.focusId ? 1 : undefined,
    hasNestedScroll: !!memoizedLines.length,
  })

  useEffect(() => {
    setFocus(focusId)
    getHomePage(search)
    return () => {
      setPage(null)
    }
  }, [])

  const pageImg = useFindNearestByImgWidth(page?.backgroundImage?.sources, 1920)
  const logout = () => navigate(paths.LOGOUT)

  const { isBroadcastVisible, playerContainerRef, recalculate } =
    useIsBroadcastVisible()

  const scrollToSection = () => {
    scroller.scrollTo('HomeElement_broadcast', scrollOptions.HomeScroll)
  }

  return (
    <>
      {pageImg?.url && <PageBackground src={pageImg.url} />}
      <Container
        defaultFocusChild={defaultFocusChild}
        focusId={focusId}
        elementType="div"
        orientation={orientation.VERTICAL}
        onMove={onMove}
        onBack={logout}
        className="content-wrapper"
      >
        <Header />
        {/* <Broadcast
          isMuted={!isBroadcastVisible}
          channelsList={classicHome?.channels}
          isClassicHome={true}
          broadcastId={constantsBroadcastIds.CLASSIC_HOME}
          scrollToSection={scrollToSection}
        /> */}
        {classicHome && (
          <ScrollContainer
            onScroll={recalculate}
            id={scrollOptions.HomeScroll.containerId}
            height={scrollOptions.HomeScroll.height}
            $withMargin
          >
            <Element
              ref={playerContainerRef}
              key="home_broadcast"
              name="HomeElement_broadcast"
            >
              <Broadcast
                isMuted={!isBroadcastVisible}
                channelsList={classicHome?.channels}
                isClassicHome={true}
                broadcastId={constantsBroadcastIds.CLASSIC_HOME}
                scrollToSection={scrollToSection}
              />
            </Element>
            <Element key="home_lines" name="LinesGroup">
              {!!memoizedLines.length && (
                <>
                  {/* LinesGroup */}
                  {!appSettings.isDisabledLines ? (
                    <Suspense>
                      <LinesGroup
                        groupData={memoizedLines}
                        scrollOptions={scrollOptions.HomeScroll}
                        isNested={true}
                        resetNestedFocusChild={!defaultFocusChild}
                        anchorId={page.anchorId}
                        initFocusId={page.focusId}
                      />
                    </Suspense>
                  ) : (
                    <Button
                      focusId={`HomePageStageEnableLinesButton`}
                      text="Enable Lines"
                      onClick={() => {
                        toggleAppSettings({ isDisabledLines: false })
                      }}
                    />
                  )}
                </>
              )}
            </Element>
          </ScrollContainer>
        )}
        {page && !classicHome && (
          <ContentUnavailableWrapper>
            <ContentUnavailable>{t('contentUnavailable')}</ContentUnavailable>
          </ContentUnavailableWrapper>
        )}
      </Container>
    </>
  )
}

export const HomePage = memo(HomeRaw)
